/**
 * +field: the quest tracker on the right (where the keystone timer sits in a key): map name and
 * level band, the zone's intro-chain step and every accepted bounty with a thin progress bar;
 * finished ones turn gold with "Turn in at …". In a levelled dungeon it shows the dungeon and a
 * Leave button. Rebuilt only when fieldView.trackerVersion changes.
 */
import type { HudWidget } from '../widgets';
import { el, setText } from '../dom';
import { fieldView } from '../../world/fieldView';
import '../quests/quests.css'; // +quests

/** +quests: the side-quest section's collapsed state (per viewer). */
const SIDE_KEY = 'keystone.tracker.sideCollapsed';
function loadCollapsed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(SIDE_KEY) === '1';
  } catch {
    return false;
  }
}
function saveCollapsed(v: boolean): void {
  try {
    localStorage.setItem(SIDE_KEY, v ? '1' : '0');
  } catch {
    /* private mode */
  }
}

export class QuestTracker implements HudWidget {
  readonly root: HTMLElement;
  private readonly map: HTMLElement;
  private readonly band: HTMLElement;
  private readonly list: HTMLElement;
  private readonly leave: HTMLButtonElement;
  private version = -1;
  private active: boolean | null = null;
  private last = new Map<string, number>();
  private sideCollapsed = loadCollapsed(); // +quests

  constructor(host: HTMLElement) {
    this.root = el('section', 'fx-trk hk-panel-fade', host);
    this.root.setAttribute('aria-label', 'Quests');
    const head = el('div', 'fx-trk-head', this.root);
    this.map = el('div', 'fx-trk-map hk-title gold', head);
    this.band = el('div', 'fx-trk-band hk-num', head);
    // +quests: the quest log button (Y).
    const log = el('button', 'fx-trk-log', head, 'Log');
    log.type = 'button';
    log.title = 'Quest log (Y)';
    log.addEventListener('click', () => fieldView.actions?.toggleQuestLog?.());
    el('div', 'hk-rule', this.root);
    this.list = el('div', 'fx-trk-list', this.root);
    this.leave = el('button', 'fx-trk-leave hk-hit', this.root, 'Leave dungeon');
    this.leave.type = 'button';
    this.leave.addEventListener('click', () => fieldView.actions?.leaveDungeon?.());
  }

  update(): void {
    const v = fieldView;
    if (v.active !== this.active) {
      this.active = v.active;
      this.root.classList.toggle('hk-hidden', !v.active);
    }
    if (!v.active || v.trackerVersion === this.version) return;
    this.version = v.trackerVersion;
    setText(this.map, v.mapName);
    setText(this.band, v.band);
    this.leave.classList.toggle('hk-hidden', v.mapKind !== 'dungeon');
    this.list.replaceChildren();
    const seen = new Map<string, number>();
    if (!v.tracker.length) el('div', 'fx-trk-empty', this.list, v.mapKind === 'town' ? 'Visit the Bounty Board for work.' : 'No bounties. Pick some up in town.');
    // +quests: side quests sit under the main quest and the bounties, in a collapsible section.
    const sides = v.tracker.filter((r) => r.kind === 'quest').length;
    let sideHead = false;
    for (const r of v.tracker) {
      if (r.kind === 'quest' && !sideHead) {
        sideHead = true;
        const h = el('div', `fx-trk-side${this.sideCollapsed ? ' collapsed' : ''}`, this.list);
        el('span', '', h, `Side quests (${sides})`);
        el('span', 'tw', h, '\u25BE');
        h.addEventListener('click', () => {
          this.sideCollapsed = !this.sideCollapsed;
          saveCollapsed(this.sideCollapsed);
          this.version = -1;
        });
      }
      if (r.kind === 'quest' && this.sideCollapsed) continue;
      const row = el('div', `fx-trk-row kind-${r.kind}${r.done ? ' done' : ''}${r.main ? ' main' : ''}${r.tracked ? ' tracked' : ''}${r.grey ? ' grey' : ''}`, this.list);
      if (r.kind === 'quest') {
        row.title = r.tracked ? 'Click: stop tracking (the arrow returns to the main quest)' : 'Click: point the arrow here';
        row.addEventListener('click', () => fieldView.actions?.trackQuest?.(r.id));
      }
      if (r.main) el('div', 'fx-trk-tag', row, 'Main quest');
      el('div', 'fx-trk-title', row, r.title);
      const line = el('div', 'fx-trk-line', row);
      el('span', 'fx-trk-text', line, r.done && r.hint ? r.hint : r.text);
      if (r.max > 1) el('span', 'fx-trk-count hk-num', line, `${r.cur}/${r.max}`);
      if (r.max > 1) {
        const bar = el('div', 'fx-trk-bar', row);
        el('div', 'fx-trk-fill', bar).style.transform = `scaleX(${Math.min(1, r.cur / r.max)})`;
      }
      // A row that just moved pulses once.
      const prev = this.last.get(r.id);
      if (prev !== undefined && prev !== r.cur) row.classList.add('bump');
      seen.set(r.id, r.cur);
    }
    this.last = seen;
  }

  dispose(): void {
    this.root.remove();
  }
}
